import React, { useEffect } from "react";
import axios from "axios";
import { WebChat, DEFAULT_WEBCHAT_THEME } from "@42maru/webchat";
import Favicon from "react-favicon";
import useMetaTags from "react-metatags-hook";
import VisibilitySensor from "react-visibility-sensor";
import authStore from "./store/AuthStore";
import mobile from "is-mobile";

interface WebChatAppProps {
    scenarioIdOrAlias: string;
    isTest?: boolean;
}

const readPublishData = (scenarioId: string) => {
    return axios
        .get(`${process.env.REACT_APP_API_HOST}/scenario/${scenarioId}/publisher`)
        .then((response) => Promise.resolve(response.data))
        .catch((error) => Promise.reject(error));
};

const getScenarioFromAlias = (alias: string) => {
    return axios
        .get(`${process.env.REACT_APP_API_HOST}/webchatalias/${alias}`)
        .then((response) => Promise.resolve(response.data))
        .catch((error) => Promise.reject(error));
};

const WebChatApp: React.FC<WebChatAppProps> = ({ scenarioIdOrAlias, isTest }) => {
    const [publishData, setPublishData] = React.useState<any>();
    const [initialized, setInitialized] = React.useState<boolean>(false);
    const [dimensions, setDimensions] = React.useState({
        height: window.innerHeight,
        width: window.innerWidth
    });
    const [scenarioId, setScenarioId] = React.useState<string>();

    useEffect(() => {
        const handleResize = () => {
            let width = window.innerWidth;
            let height = window.innerHeight;

            setDimensions({
                height: height,
                width: width
            });
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [scenarioIdOrAlias]);

    useEffect(() => {
        getScenarioFromAlias(scenarioIdOrAlias)
            .then((response) => {
                setScenarioId(response.data.scenario_id);
                readPublishData(response.data.scenario_id)
                    .then((response) => {
                        setPublishData(response.data);
                        setInitialized(true);
                    })
                    .catch(() => {
                        setInitialized(true);
                    });
            })
            .catch(() => {
                setScenarioId(scenarioIdOrAlias);
                readPublishData(scenarioIdOrAlias)
                    .then((response) => {
                        setPublishData(response.data);
                        setInitialized(true);
                    })
                    .catch(() => {
                        setInitialized(true);
                    });
            });
    }, [scenarioIdOrAlias]);

    let theme = (publishData && publishData.webchat_theme) || DEFAULT_WEBCHAT_THEME;
    const title = theme.title || "WebChat";
    document.title = title;
    theme = { ...theme, showLogoutButton: true };

    useMetaTags(
        {
            title: `${title}`,
            charset: "utf8",
            metas: [
                { name: "type", content: `website` },
                { name: "description", content: `${theme.description}` },
                { "http-equiv": "Cache-Control", content: "no-cache" }
            ],
            openGraph: {
                title: `${title}`,
                image: `${theme.avatarUrl}`,
                site_name: `${title}`
            }
        },
        [title, theme]
    );

    if (!initialized || !scenarioId) {
        return null;
    }

    const splitUsername = authStore.loginUser?.username.split('\\');
    let empNo = '';
    if (splitUsername && splitUsername.length === 2) {
        empNo = splitUsername[1];
    }

    return (
        <VisibilitySensor
            onChange={(isVisible) => {
                if (isVisible) {
                }
            }}
        >
            <div>
                <Favicon url={theme.faviconUrl || ""} />
                <WebChat
                    connectionConfig={{
                        apiHost: `${process.env.REACT_APP_API_HOST}`,
                        socketHost: `${process.env.REACT_APP_SOCKET_HOST}`,
                        method: "socket",
                        test: isTest
                    }}
                    style={{
                        width: dimensions.width || "100vw",
                        height: dimensions.height || "100vh"
                    }}
                    scenarioId={scenarioId}
                    theme={theme}
                    params={{ agent: mobile() ? 'mobile' : 'pc', emp_no: empNo }}
                    onClickLogoutButton={() => {
                        window.location.href = `${process.env.REACT_APP_API_HOST}/auth/logout?logout_uri=${process.env.REACT_APP_LOGOUT_CALLBACK_URI}&federated=true`
                    }}
                />
            </div>
        </VisibilitySensor>
    );
};

export default WebChatApp;
